import React, { useEffect, useState } from 'react';
import { useForm } from '../forms/useForm';
import moment from 'moment';
import 'moment/locale/es';
import { sendRequest } from "../../hooks/requests/useRequest";
import { ConfigServer } from "../../data/config";

export const useReportCxc = () => {
    const [reports, setReports] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [visualProgress, setVisualProgress] = useState({});

    const [listCompanies, setListCompanies] = useState([]);
    const [id_company, setIdCompany] = useState(null);

    const [listAgreements, setListAgreements] = useState([]);
    const [idAgreementCompany, setIdAgreementCompany] = useState(null);

    //? TOAST ALERT 
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState("info");
    const [toastTitle, setToastTitle] = useState("");
    const [toastMessage, setToastMessage] = useState("");
    
    const [formValues, handleInputChange] = useForm({
        start_date: "",
        end_date: ""
    });

    const {
        start_date,
        end_date
    } = formValues;

    //#region ALERT GLOBAL 
    const showToastAlert = (
        type = "info",
        title = "",
        message = ""
    ) => {

        setToastType(type);
        setToastTitle(title);
        setToastMessage(message);
        setShowToast(true);

    };

    const closeToastAlert = () => {
        setShowToast(false);
        setToastTitle("");
        setToastMessage("");
    };
    //#endregion
    //? LISTAR REPORTES GENERADOS
    const getReportsCxC = async () => {
        let requestOptions = {
            method: 'GET'
        };

        const respuesta = await sendRequest(
            requestOptions,
            "Reports/ListReportCxCDownloads"
        );

        if (respuesta.code === 200) {
            setReports(respuesta.data ?? []);
        }
    };

    //#region GENERATE REPORT
    const generateReportCxC = async () => {
        if (!start_date || !end_date) {
            showToastAlert("warning", "Aviso", "Selecciona la fecha de inicio y fecha de cierre.");
            return;
        }

        if (moment(start_date).isAfter(moment(end_date))) {
            showToastAlert("warning", "Aviso", "La fecha de inicio no puede ser mayor a la fecha de cierre.");
            return;
        }

        setIsDownloading(true);

        let requestOptions = {
            method: 'POST'
        };

        try {
            let url = `Reports/GenerateReportCxC/${start_date}/${end_date}`;

            const params = new URLSearchParams();

            if (id_company?.value) {
                params.append("id_company", id_company.value);
            }

            if (idAgreementCompany?.value) {
                params.append("id_agreement", idAgreementCompany.value);
            }

            const queryString = params.toString();

            if (queryString) {
                url += `?${queryString}`;
            }

            const respuesta = await sendRequest(
                requestOptions,
                url
            );

            if (respuesta.code === 200) {
                showToastAlert("success", "Reporte", "El reporte se está generando.");
                await getReportsCxC();
            }
            else {
                showToastAlert("error", "Error", respuesta.data?.msg || "No fue posible generar el reporte");
            }
        }
        catch (error) {
            console.error("Error al generar reporte CxC:",error);
        }
        finally {
            setIsDownloading(false);
        }
    };
    //#endregion

    //#region DOWNLOAD REPORT
    const downloadReportCxC = async (row) => {
        if (!row || Number(row.progress) < 100) {
            return;
        }

        let token = localStorage.getItem("token");
        await fetch(ConfigServer.serverUrl + `/api/Reports/DownloadReportCxCFile/${row.id_report_cxc_download}`, {
            method: 'GET',
            headers: new Headers({
                "Authorization": "Bearer " + token,
                "Content-Type": "application/vnd.ms-excel"
            })
        })
            .then(response => {
                if (response.status !== 200) {
                    showToastAlert("warning", "Error", "No se descargo el archivo excel");
                    return;
                }

                return response.blob();
            })
            .then(blob => {
                var url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "Reporte global de cuentas por cobrar.xlsx");
                document.body.appendChild(link);
                link.style.display = 'none';
                link.click();
            });
        }
    //#endregion

    //? CARGAR REPORTES AL ENTRAR
    useEffect(() => {
        getReportsCxC();
    }, []);

    //#region VISUAL PROGRESS
    useEffect(() => {
        const interval = setInterval(() => {
            setVisualProgress(prev => {
                const updated = { ...prev };

                reports.forEach(report => {
                    const id = report.id_report_cxc_download;
                    const backendProgress = Number(report.progress) || 0;

                    if (backendProgress >= 100) {
                        updated[id] = 100;
                        return;
                    }

                    let current = updated[id] ?? 5;

                    if (current < 25) {
                        current += 5;
                    }
                    else if (current < 50) {
                        current += 3;
                    }
                    else if (current < 75) {
                        current += 2;
                    }
                    else if (current < 95) {
                        current += 1;
                    }

                    updated[id] = Math.min(current, 95);
                });

                return updated;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [reports]);
    //#endregion

    //#region UPDATE PROGRESS AUTOMATICALLY
    useEffect(() => {
        const hasPending = reports.some(x => Number(x.progress) < 100);

        if (!hasPending) {
            return;
        }

        const timeout = setTimeout(() => {
            getReportsCxC();
        }, 5000);

        return () => clearTimeout(timeout);
    }, [reports]);
    //#endregion

    //#region SEARCH
    const filteredReports = reports.filter(item => {
        if (!searchTerm) {
            return true;
        }

        const search = searchTerm.toLowerCase();

        return Object.values(item).some(value =>
            String(value ?? "")
                .toLowerCase()
                .includes(search)
        );
    });
    //#endregion

    const isButtonDisabled =
        !start_date ||
        !end_date ||
        isDownloading;
   
    const handleGetClients = async (typeClient) => {
        let requestOptions = {
            method: 'GET'
        };

        const respuesta = await sendRequest(
            requestOptions,
            `Company/ListTypeCompanies/${typeClient}`
        );

        if (respuesta.code === 200) {
            const listTmp = respuesta.data ?? [];
            let list = [];

            listTmp.forEach(obj => {
                if (obj?.block_company === 0) return;

                list.push({
                    value: obj.id_company,
                    label: obj.name,
                    payment_by_company: obj.payment_by_company,
                    payment_by_patient: obj.payment_by_patient,
                    require_invoice: obj.require_invoice,
                    available_invoice_patient: obj.available_invoice_patient
                });
            });

            setListCompanies(list);
        }
        else {
            setListCompanies([]);
        }
    };

    const handleChangeClient = (option) => {
        setIdCompany(option);
        setIdAgreementCompany(null);
        setListAgreements([]);
    };

    const handleGetAgreements = async (idCompany) => {
        if (!idCompany) {
            setListAgreements([]);
            return;
        }

        let requestOptions = {
            method: 'GET'
        };

        const respuesta = await sendRequest(
            requestOptions,
            `Agreement/ListAgreementByCompany/${idCompany}`
        );

        if (respuesta.code === 200) {
            const listTmp = respuesta.data ?? [];

            const list = listTmp.map(agreement => ({
                value: agreement.id_agreement,
                label: agreement.code
            }));

            setListAgreements(list);
        }
        else {
            setListAgreements([]);
        }
    };

    const handleChangeAgreement = (option) => {
        setIdAgreementCompany(option);
    };


    useEffect(() => {
        if (id_company?.value) {
            handleGetAgreements(id_company.value);
        }
        else {
            setListAgreements([]);
            setIdAgreementCompany(null);
        }
    }, [id_company]);

    useEffect(() => {
        getReportsCxC();
        handleGetClients(1);
    }, []);
    return {
        start_date,
        end_date,
        handleInputChange,
        reports,
        filteredReports,
        isDownloading,
        isButtonDisabled,
        generateReportCxC,
        downloadReportCxC,
        getReportsCxC,
        searchTerm,
        setSearchTerm,
        visualProgress,
        listCompanies,

        //? FILTER CLIENTE
        id_company,
        handleChangeClient,

        //?  FILTER AGREEMENT
        listAgreements,
        idAgreementCompany,
        handleChangeAgreement,

        //? TOAST ALERT
        showToast,
        toastType,
        toastTitle,
        toastMessage,
        showToastAlert,
        closeToastAlert
    };
};